import React, { useState } from "react";
import TagWrapper from "app/patch/components/Tag/SeverityTagWrapper";
import Modal from "common-ui-components/components/modal";
import PatchOverview from "app/inventory-new/devices/device/patch/patch tab comps/PatchOverview";
import { toTitleCase } from "common-ui-components/components/utils/string-utils";
import Select from "common-ui-components/components/select";
import { notify } from "common-ui-components/components/notification/Notification";
import {
  Success,
  Failure,
} from "common-ui-components/components/notification/templates";
import API from "common-ui-components/components/utils/API";

const HighlightedTextWrapper = ({ children }) => {
  return (
    <div
      style={{
        color: "#2F7EE9",
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {children}
    </div>
  );
};

const PatchIdClickableWrapper = ({ children, record }) => {
  const [isPatchModalOpen, setIsPatchModalOpen] = React.useState(false);

  return (
    <>
      <span
        style={{
          color: "#1976d2",
          cursor: "pointer",
        }}
        onClick={(event) => {
          event.stopPropagation();
          setIsPatchModalOpen(true);
        }}
      >
        {children}
      </span>
      <Modal open={isPatchModalOpen} setOpen={setIsPatchModalOpen}>
        <div
          className="scrollBarOnhover"
          style={{
            height: "95vh",
            width: "85vw",
          }}
        >
          <PatchOverview patch={record} />
        </div>
      </Modal>
    </>
  );
};

export const SeverityTagWrapper = ({ record: incomingRecord, colorMap }) => {
  const severity = incomingRecord?.severity;
  const normalizedSeverity = `${severity || ""}`.trim().toLowerCase();
  const record =
    normalizedSeverity === "n/a" || normalizedSeverity === "na"
      ? { ...incomingRecord, severity: "unknown" }
      : incomingRecord;

  return <TagWrapper record={record} colorMap={colorMap} />;
};

const ApproveStatus = ({ record, refreshTable }) => {
  const statusOptions = ["Approved", "Not Approved"];
  const [approved, setApproved] = useState(!!record?.approved);
  const [submitting, setSubmitting] = useState(false);

  const activeIndex = approved ? 0 : 1;

  const handleOptionSelect = async (_option, index) => {
    const nextApproved = index === 0;
    if (nextApproved === approved || submitting) return;

    const patchId = record?.orgPatchId ?? record?.patchId ?? record?.id;
    if (patchId === undefined || patchId === null) {
      notify(<Failure>Patch id is missing.</Failure>);
      return;
    }

    const previousApproved = approved;
    setApproved(nextApproved); // optimistic update
    setSubmitting(true);
    try {
      const response = await API.post("patch/approvePatch", {
        patchIds: [patchId],
        approve: nextApproved,
      });
      if (response.status.success) {
        notify(<Success>Patch approval status changed successfully.</Success>);
        refreshTable?.();
      } else {
        setApproved(previousApproved); // revert on failure
        notify(<Failure>Failed to update approval status.</Failure>);
      }
    } catch (err) {
      setApproved(previousApproved); // revert on failure
      notify(<Failure>Failed to update approval status.</Failure>);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Select
      active={activeIndex}
      options={statusOptions}
      onOptionSelect={handleOptionSelect}
      disabled={submitting}
      iconProps={{ iconVariant: "chevron" }}
    />
  );
};

const ApprovedTextWrapper = ({ record }) => (
  <span>{record?.approved ? "Approved" : "Not Approved"}</span>
);

export default ApproveStatus;

export const COMMON_PATCH_COLUMNS = [
  {
    key: "patchId",
    title: "Patch Id",
    accessor: "masterPatchId",
    width: 8.24,
    headerLines: 2,
    WrapperComponent: PatchIdClickableWrapper,
  },
  {
    key: "title",
    title: "Title",
    accessor: "title",
    width: 20,
    lines: 2,
  },
  {
    key: "missingCount",
    title: "Missing Systems",
    accessor: "missingCount",
    width: 8,
    headerLines: 2,
    WrapperComponent: HighlightedTextWrapper,
  },
  {
    key: "installCount",
    title: "Installed Systems",
    accessor: "installCount",
    width: 8,
    headerLines: 2,
    WrapperComponent: HighlightedTextWrapper,
  },
  // {
  //   key: 'patchCategory',
  //   title: 'Category',
  //   accessor: 'patchCategory',
  // },
  {
    key: "patchFamily",
    title: "Patch Family",
    accessor: "patchFamily",
    width: 10,
  },
  {
    key: "approved",
    title: "Approval Status",
    accessor: "approved",
    WrapperComponent: ApproveStatus,
  },
  {
    key: "severityTag",
    title: "Severity",
    accessor: "severity",
    WrapperComponent: SeverityTagWrapper,
    width: 10,
  },

  {
    key: "installStatus",
    title: "Install Status",
    accessor: "installStatus",
  },
  {
    key: "rebootRequired",
    title: "Reboot",
    accessor: "rebootRequired",
    unsortable: true,
    width: 15,
    WrapperComponent: ({ record }) => (
      <span
        style={{
          color:
            record?.rebootRequired?.toLowerCase() === "not needed"
              ? "green"
              : "blue",
        }}
      >
        {toTitleCase(record?.rebootRequired)}
      </span>
    ),
  },
  {
    key: "patchType",
    title: "Patch Type",
    accessor: "patchType",
    initiallyHidden: true,
  },
  // {
  //   key: "patchCategory",
  //   title: "Category",
  //   accessor: "patchCategory",
  //   initiallyHidden: true,
  // },
  {
    key: "description",
    title: "Patch Description",
    accessor: "description",
    initiallyHidden: true,
    width: 30,
    lines: 2,
  },
  {
    key: "kbArticleId",
    title: "KB Number",
    accessor: "kbArticleId",
    initiallyHidden: true,
  },
  // {
  //   key: 'patchType',
  //   title: 'Patch Type',
  //   accessor: 'patchType',
  //   initiallyHidden: true,
  // },
  {
    key: "requiresUserInput",
    title: "Requires User Input",
    accessor: "requiresUserInput",
    initiallyHidden: true,
  },
  {
    key: "isMandatory",
    title: "Mandatory",
    accessor: "isMandatory",
    initiallyHidden: true,
  },
  {
    key: "isUninstallable",
    title: "Uninstallable",
    accessor: "isUninstallable",
    initiallyHidden: true,
  },
];

const PATCH_COLUMN_BY_KEY = COMMON_PATCH_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column;
  return acc;
}, {});

const ORG_PATCH_COLUMNS = COMMON_PATCH_COLUMNS.map((col) => col.key);
const COLUMNS_TO_REMOVE = new Set(["missingCount", "installCount"]);
const DEVICE_PATCH_COLUMNS = ORG_PATCH_COLUMNS.filter(
  (col) => !COLUMNS_TO_REMOVE.has(col),
);

const MISSING_TABLE_FILTER = new Set(["installCount", "installStatus"]);
const INSTALL_TABLE_FILTER = new Set(["missingCount", "installStatus"]);
const ALL_TABLE_FILTER = new Set(["installStatus"]);

const PATCH_TABLE_COLUMNS = {
  org: {
    missing: ORG_PATCH_COLUMNS.filter((col) => !MISSING_TABLE_FILTER.has(col)),
    installed: ORG_PATCH_COLUMNS.filter(
      (col) => !INSTALL_TABLE_FILTER.has(col),
    ),
    all: ORG_PATCH_COLUMNS.filter((col) => !ALL_TABLE_FILTER.has(col)),
  },
  device: {
    missing: DEVICE_PATCH_COLUMNS.filter(
      (col) => !MISSING_TABLE_FILTER.has(col),
    ),
    installed: DEVICE_PATCH_COLUMNS.filter(
      (col) => !INSTALL_TABLE_FILTER.has(col),
    ),
    all: DEVICE_PATCH_COLUMNS.filter((col) => !ALL_TABLE_FILTER.has(col)),
  },
};

export const getPatchColumns = (scope = "org", tableType = "all") => {
  const tableColumns = PATCH_TABLE_COLUMNS[scope] || PATCH_TABLE_COLUMNS.org;
  const columnKeys = tableColumns[tableType] || tableColumns.all;

  return columnKeys.map((key) => {
    const column = { ...PATCH_COLUMN_BY_KEY[key] };
    delete column.key;

    if (scope === "device" && key === "approved") {
      column.WrapperComponent = ApprovedTextWrapper;
    }

    return { ...column };
  });
};

export const getCommonPatchFilterConfig = () => ({
  quick_filters: [
    {
      column: "patchFamily",
      options: [
        { label: "All Patches", value: ["ALL"] },
        { label: "OS", value: ["OS"] },
        { label: "Software", value: ["SOFTWARE"] },
        { label: "Driver", value: ["DRIVER"] },
      ],
    },
  ],
});

export const getSeverityFilterConfig = () => ({
  quick_filters: [
    {
      column: "severity",
      options: [
        { label: "All Severity", value: ["ALL"] },
        { label: "Critical", value: ["CRITICAL"] },
        { label: "Important", value: ["IMPORTANT"] },
        { label: "Moderate", value: ["MODERATE"] },
        { label: "Low", value: ["LOW"] },
        { label: "Unknown", value: ["UNKNOWN"] },
      ],
    },
  ],
});
